import { type CategoryRepository } from "../interfaces";
import { type DatabaseCategory } from "@/hooks/useCategories";
import { supabase } from "@/integrations/supabase/supabaseClient";

export class SupabaseCategoryRepository implements CategoryRepository {
  private async getCurrentUserId(): Promise<string> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");
    return user.id;
  }

  async getAll(): Promise<DatabaseCategory[]> {
    const userId = await this.getCurrentUserId();
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .eq("user_id", userId)
      .order("name");

    if (error) throw error;
    return data as DatabaseCategory[];
  }

  async create(
    category: Omit<
      DatabaseCategory,
      "id" | "user_id" | "created_at" | "updated_at"
    >,
  ): Promise<DatabaseCategory> {
    const userId = await this.getCurrentUserId();
    const { data, error } = await supabase
      .from("categories")
      .insert({
        ...category,
        user_id: userId,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async update(
    id: string,
    updates: Partial<DatabaseCategory>,
  ): Promise<DatabaseCategory> {
    const userId = await this.getCurrentUserId();
    const { data, error } = await supabase
      .from("categories")
      .update(updates)
      .eq("id", id)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async delete(id: string): Promise<void> {
    const userId = await this.getCurrentUserId();
    const { error } = await supabase
      .from("categories")
      .delete()
      .eq("id", id)
      .eq("user_id", userId);

    if (error) throw error;
  }

  async bulkCreate(
    categories: Omit<
      DatabaseCategory,
      "id" | "user_id" | "created_at" | "updated_at"
    >[],
  ): Promise<DatabaseCategory[]> {
    const userId = await this.getCurrentUserId();
    const categoriesWithUserId = categories.map((cat) => ({
      ...cat,
      user_id: userId,
    }));

    const { data, error } = await supabase
      .from("categories")
      .insert(categoriesWithUserId)
      .select();

    if (error) throw error;
    return data;
  }

  async deleteAll(): Promise<void> {
    const userId = await this.getCurrentUserId();
    const { error } = await supabase
      .from("categories")
      .delete()
      .eq("user_id", userId);

    if (error) throw error;
  }
}
